/**
 * The media library.
 *
 * Two things here are less obvious than they look.
 *
 * The first is that "everything I can reach" is several requests, not one.
 * Assets live in one table partition per store plus the shared `GLOBAL` library,
 * so a listing is a Query against a partition; asking for six scopes asks six
 * times and merges. That is deliberate — the alternative is a scan — and it is
 * why the owner filter feeds the query rather than filtering a list already in
 * hand.
 *
 * The second is that a file is judged in the browser before a byte moves. The
 * upload queue therefore has a "not accepted" state that is not an error state:
 * an iPhone shooting in its default High Efficiency mode produces exactly the
 * file this refuses, and the row that refuses it has to name the setting to
 * change rather than say "unsupported format".
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { Modal } from '../components/Modal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { ErrorBlock, InfoNote, InlineEmpty, LoadingBlock } from '../components/Feedback.js';
import { MediaDropzone } from '../components/media/MediaDropzone.js';
import type { ScopeOption } from '../components/media/MediaDropzone.js';
import { ALL_SCOPES, MediaFilters, selectedScopes } from '../components/media/MediaFilters.js';
import type { ScopeSelection } from '../components/media/MediaFilters.js';
import { MediaGrid } from '../components/media/MediaGrid.js';
import { MediaEditForm } from '../components/media/MediaEditForm.js';
import { UploadQueueList } from '../components/media/UploadQueue.js';
import { filterMedia, parseBrands, scopeLabel } from '../components/media/presentation.js';
import type { MediaKindFilter } from '../components/media/presentation.js';
import { useUploadQueue } from '../components/media/useUploadQueue.js';
import { errorMessage } from '../lib/api.js';
import { canReachStore, hasNoScope, isAdmin } from '../lib/access.js';
import {
  MEDIA_QUERY_KEY,
  PLAYLIST_USAGE_QUERY_KEY,
  deleteMedia,
  listMedia,
  listPlaylistUsage,
  mediaScopeQueryKey,
  playlistsUsingAsset,
  updateMedia,
} from '../lib/media.js';
import type { MediaAsset, MediaUpdate, PlaylistUsage } from '../lib/media.js';
import { useSession } from '../lib/session.js';
import { STORES_QUERY_KEY, listStores } from '../lib/stores.js';
import { useNow } from '../lib/useNow.js';
import '../components/media/media.css';

type Dialog =
  | { kind: 'none' }
  | { kind: 'edit'; asset: MediaAsset }
  | { kind: 'delete'; asset: MediaAsset };

export function MediaPage(): ReactElement {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const nowMs = useNow(60_000);

  const [selection, setSelection] = useState<ScopeSelection>(ALL_SCOPES);
  const [kind, setKind] = useState<MediaKindFilter>('all');
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const [uploadStoreId, setUploadStoreId] = useState<string | null | undefined>(undefined);
  const [uploadBrands, setUploadBrands] = useState('');

  const closeDialog = (): void => setDialog({ kind: 'none' });

  const stores = useQuery({
    queryKey: STORES_QUERY_KEY,
    queryFn: ({ signal }) => listStores(signal),
  });

  const storeNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const store of stores.data ?? []) names.set(store.storeId, store.name);
    return names;
  }, [stores.data]);

  /**
   * The shared library first, because it is the one every store draws on, then
   * the stores this account can actually reach. A manager sees their own; an
   * admin sees the estate.
   */
  const scopes = useMemo<ScopeOption[]>(() => {
    const reachable = (stores.data ?? [])
      .filter((store) => canReachStore(user, store.storeId))
      .sort((left, right) => left.name.localeCompare(right.name))
      .map<ScopeOption>((store) => ({ storeId: store.storeId, label: store.name }));
    return [{ storeId: null, label: 'Shared library' }, ...reachable];
  }, [stores.data, user]);

  const activeScopes = useMemo(() => selectedScopes(scopes, selection), [scopes, selection]);

  /**
   * One query per scope rather than one query for the page, keyed the way
   * `mediaScopeQueryKey` says. The playlist editor's asset picker asks for the
   * same scopes under the same keys, so opening it after browsing the library
   * costs nothing, and one upload invalidates both through the shared prefix.
   */
  const mediaResults = useQueries({
    queries: activeScopes.map((scope) => ({
      queryKey: mediaScopeQueryKey(scope.storeId),
      queryFn: ({ signal }: { signal: AbortSignal }) => listMedia(scope.storeId, signal),
    })),
  });

  const mediaPending = mediaResults.some((result) => result.isPending);
  const mediaError = mediaResults.find((result) => result.isError)?.error ?? null;
  const refetchMedia = (): void => {
    for (const result of mediaResults) void result.refetch();
  };

  // Only read when someone is about to delete something. It is the playlist
  // editor's route, and the library has no other reason to hold it.
  const usage = useQuery({
    queryKey: PLAYLIST_USAGE_QUERY_KEY,
    queryFn: ({ signal }) => listPlaylistUsage(signal),
    enabled: dialog.kind === 'delete',
    retry: 0,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY });
  };

  const uploads = useUploadQueue(() => {
    void invalidate();
  });

  const update = useMutation({
    mutationFn: ({ asset, patch }: { asset: MediaAsset; patch: MediaUpdate }) =>
      updateMedia(asset.storeId, asset.assetId, patch),
    onSuccess: async () => {
      await invalidate();
      closeDialog();
    },
  });

  const remove = useMutation({
    mutationFn: (asset: MediaAsset) => deleteMedia(asset.storeId, asset.assetId),
    onSuccess: async () => {
      await invalidate();
      closeDialog();
    },
  });

  const assets = mediaResults.flatMap((result) => result.data ?? []);
  const visible = filterMedia(assets, { kind, query });

  // Uploading into the shared library writes content every brand can reach, so
  // it is admin-only — the same rule the API enforces on the GLOBAL partition.
  const uploadScopes = useMemo<ScopeOption[]>(
    () => scopes.filter((scope) => scope.storeId !== null || isAdmin(user)),
    [scopes, user],
  );

  // A store, not the shared library, unless a store is not on offer. Most files
  // belong to one shop, and shared content reaches every brand's screens — that
  // is a choice to make deliberately rather than to fall into.
  const defaultUploadStore =
    uploadScopes.find((scope) => scope.storeId !== null)?.storeId ?? uploadScopes[0]?.storeId ?? null;
  const chosenUploadStore = uploadStoreId === undefined ? defaultUploadStore : uploadStoreId;
  const uploadStore = uploadScopes.some((scope) => scope.storeId === chosenUploadStore)
    ? chosenUploadStore
    : defaultUploadStore;

  const canEditAsset = (asset: MediaAsset): boolean =>
    asset.storeId === null ? isAdmin(user) : canReachStore(user, asset.storeId);

  const blockedReason =
    uploadScopes.length === 0
      ? 'Your account has not been given a store yet, so there is nowhere to put a file. An administrator needs to add you to one.'
      : null;

  return (
    <>
      <PageHeader
        title="Media"
        description="Videos and images, owned by one store or shared across brands. Every file is checked in this browser before it is sent, and again on the server before it goes live."
      />

      <MediaDropzone
        scopes={uploadScopes}
        storeId={uploadStore}
        onStoreChange={setUploadStoreId}
        brands={uploadBrands}
        onBrandsChange={setUploadBrands}
        blockedReason={blockedReason}
        onFiles={(files) =>
          uploads.add(files, {
            storeId: uploadStore,
            brands: uploadStore === null ? parseBrands(uploadBrands) : [],
          })
        }
      />

      <UploadQueueList
        items={uploads.items}
        onCancel={uploads.cancel}
        onDismiss={uploads.dismiss}
        onClearSettled={uploads.clearSettled}
      />

      {stores.isError ? (
        <ErrorBlock
          error={stores.error}
          action="load the stores"
          onRetry={() => void stores.refetch()}
        />
      ) : null}

      <MediaFilters
        scopes={scopes}
        scope={selection}
        onScopeChange={setSelection}
        kind={kind}
        onKindChange={setKind}
        query={query}
        onQueryChange={setQuery}
        shown={visible.length}
        total={assets.length}
      />

      {mediaPending && activeScopes.length > 0 ? (
        <LoadingBlock label="Loading the library…" />
      ) : null}

      {mediaError !== null ? (
        <ErrorBlock error={mediaError} action="load the media library" onRetry={refetchMedia} />
      ) : null}

      {!mediaPending && mediaError === null && assets.length === 0 ? (
        hasNoScope(user) && scopes.length === 1 ? (
          <InlineEmpty
            title="Nothing shared with you yet"
            description="Your account has no store, so the only library you can see is the shared one, and nobody has put anything in it."
          />
        ) : (
          <InlineEmpty
            title="Nothing in this library yet"
            description="Drop a video or an image above. A 1080p clip and a couple of stills are enough to give a screen something to play."
          />
        )
      ) : null}

      {mediaError === null && assets.length > 0 && visible.length === 0 ? (
        <InlineEmpty
          title="Nothing matches that filter"
          description="Widen the owner, the kind, or the search text."
        />
      ) : null}

      {visible.length > 0 ? (
        <MediaGrid
          assets={visible}
          storeNames={storeNames}
          nowMs={nowMs}
          canEdit={canEditAsset}
          onEdit={(asset) => {
            update.reset();
            setDialog({ kind: 'edit', asset });
          }}
          onDelete={(asset) => {
            remove.reset();
            setDialog({ kind: 'delete', asset });
          }}
        />
      ) : null}

      {!isAdmin(user) ? (
        <InfoNote>
          The shared library is read-only for store managers: you can put shared files on your own
          screens, but only an administrator can add to it or change what is in it.
        </InfoNote>
      ) : null}

      <Modal
        open={dialog.kind === 'edit'}
        title={dialog.kind === 'edit' ? `Edit ${dialog.asset.title || 'this file'}` : 'Edit media'}
        description="The file itself never changes — assets are immutable, so a replacement is a new upload."
        onClose={closeDialog}
      >
        {dialog.kind === 'edit' ? (
          <MediaEditForm
            key={dialog.asset.assetId}
            asset={dialog.asset}
            busy={update.isPending}
            error={update.error}
            onSubmit={(patch) => update.mutate({ asset: dialog.asset, patch })}
            onCancel={closeDialog}
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        open={dialog.kind === 'delete'}
        title={
          dialog.kind === 'delete'
            ? `Delete ${dialog.asset.title || 'this file'}?`
            : 'Delete media?'
        }
        description="The file is removed from the library and from storage. Screens drop it at their next heartbeat."
        confirmLabel="Delete file"
        busyLabel="Deleting…"
        busy={remove.isPending}
        error={remove.error === null ? null : errorMessage(remove.error)}
        onConfirm={() => {
          if (dialog.kind === 'delete') remove.mutate(dialog.asset);
        }}
        onClose={closeDialog}
      >
        {dialog.kind === 'delete' ? (
          <DeleteWarning
            asset={dialog.asset}
            playlists={usage.data ?? null}
            checking={usage.isPending && usage.fetchStatus !== 'idle'}
            failed={usage.isError}
            storeNames={storeNames}
          />
        ) : null}
      </ConfirmDialog>
    </>
  );
}

interface DeleteWarningProps {
  asset: MediaAsset;
  playlists: readonly PlaylistUsage[] | null;
  checking: boolean;
  failed: boolean;
  storeNames: ReadonlyMap<string, string>;
}

/**
 * Deleting an asset that a playlist still names leaves a dangling reference. The
 * resolver drops those silently rather than blanking a screen, so nothing
 * breaks — but a playlist quietly getting shorter is exactly the kind of change
 * someone should agree to on purpose.
 */
function DeleteWarning({
  asset,
  playlists,
  checking,
  failed,
  storeNames,
}: DeleteWarningProps): ReactElement {
  if (checking) {
    return <p className="state-block__text">Checking which playlists use this file…</p>;
  }

  if (failed || playlists === null) {
    return (
      <p className="alert alert--warning" role="status">
        The playlists could not be read, so this cannot say whether the file is still in use. A
        playlist that names a deleted file simply plays one item fewer.
      </p>
    );
  }

  const using = playlistsUsingAsset(playlists, asset.assetId);
  if (using.length === 0) {
    return <p className="state-block__text">No playlist names this file.</p>;
  }

  return (
    <div className="alert alert--warning" role="status">
      <p>
        {using.length === 1 ? 'One playlist still uses' : `${using.length} playlists still use`} this
        file. {using.length === 1 ? 'It' : 'They'} will play one item fewer.
      </p>
      <ul className="media-usage">
        {using.map((playlist) => (
          <li key={`${playlist.storeId ?? 'shared'}:${playlist.playlistId}`}>
            {playlist.name === '' ? playlist.playlistId : playlist.name}
            <span className="facts__aside"> · {scopeLabel(playlist.storeId, storeNames)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
